<template>
  <div class="fi-maplink" style="margin: 4px 20px 20px;">
    <FiPageRail title="Map Link Builder">
      <template #actions>
        <b-button size="sm" variant="success" title="How to use this page" aria-label="Help" @click="show_help = true">
          <i class="fa fa-info-circle" aria-hidden="true"></i>
        </b-button>
      </template>
    </FiPageRail>

    <b-alert v-model="alert.show" :variant="alert.variant" dismissible fade>
      {{alert.message}}
    </b-alert>

    <div class="d-flex flex-column align-items-center" style="text-align: left; color: #666;">

      <!-- Create / Edit Form -->
      <div ref="linkForm" style="width: 95%; max-width: 1400px; background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 8px; padding: 15px; margin-bottom: 25px;">
        <span style="color: var(--fi-rail-accent); font-weight: bold; font-size: 18px;">{{ editing_token ? 'Edit Map Link' : 'Create Map Link' }}</span>

        <div style="display: flex; justify-content: center; gap: 48px; margin-top: 12px; flex-wrap: wrap; align-items: flex-start;">

          <div style="flex: 0 0 auto;">
            <label class="mlb-field-label">Vehicles</label>
            <div class="mlb-veh-grid">
              <b-form-input
                v-model="vehicle_search"
                placeholder="Search available vehicles..."
                size="sm"
                style="grid-column: 1; grid-row: 1; width: 100%; margin-bottom: 20px;"
              ></b-form-input>

              <div class="mlb-list-header" style="grid-column: 1; grid-row: 2;">Available ({{ available_vehicles.length }})</div>
              <div class="mlb-list-header" style="grid-column: 3; grid-row: 2;">Selected ({{ selected_vehicles.length }})</div>

              <div class="mlb-veh-list" style="grid-column: 1; grid-row: 3;">
                <div v-for="veh in available_vehicles" :key="veh.value" class="mlb-veh-item" title="click to add" @click="select_vehicle(veh)">
                  <span>{{ veh.text }}</span><span class="mlb-veh-action">+</span>
                </div>
                <div v-if="vehicle_options.length === 0" class="mlb-veh-empty">
                  <b-spinner small></b-spinner> loading vehicles...
                </div>
                <div v-else-if="available_vehicles.length === 0" class="mlb-veh-empty">
                  {{ vehicle_search ? 'no vehicles match "' + vehicle_search + '"' : 'all vehicles selected' }}
                </div>
              </div>

              <div class="mlb-transfer-col" style="grid-column: 2; grid-row: 3;">
                <b-button size="sm" variant="outline-secondary" @click="select_all" :disabled="available_vehicles.length === 0" :title="'add all' + (vehicle_search ? ' matching' : '') + ' vehicles'">
                  <i class="fa fa-angle-double-right" aria-hidden="true"></i>
                </b-button>
                <b-button size="sm" variant="outline-secondary" @click="selected_vehicles = []" :disabled="selected_vehicles.length === 0" title="remove all vehicles">
                  <i class="fa fa-angle-double-left" aria-hidden="true"></i>
                </b-button>
              </div>

              <div class="mlb-veh-list" style="grid-column: 3; grid-row: 3;">
                <div v-for="veh in selected_vehicles" :key="veh" class="mlb-veh-item" title="click to remove" @click="unselect_vehicle(veh)">
                  <span>{{ veh }}</span><span class="mlb-veh-action">&times;</span>
                </div>
                <div v-if="selected_vehicles.length === 0" class="mlb-veh-empty">
                  click vehicles on the left to add them
                </div>
              </div>
            </div>
            <div v-if="selected_vehicles.length > 50" class="mlb-veh-warning">
              a link can include at most 50 vehicles
            </div>
          </div>

          <div style="flex: 0 0 auto; min-width: 280px; max-width: 400px;">
            <div class="mlb-field-group">
              <label class="mlb-field-label">Tracking Window</label>
              <div style="display: flex; gap: 15px; align-items: center;">
                <b-button size="sm" variant="outline-secondary" @click="shift_window_date(-1)" :disabled="!start_time || !end_time" title="Shift window back one day"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
                <b-form-input v-model="start_time" type="datetime-local" size="sm" style="width: 189px;"></b-form-input>
                <span>to</span>
                <b-form-input v-model="end_time" type="datetime-local" size="sm" style="width: 190px;"></b-form-input>
                <b-button size="sm" variant="outline-secondary" @click="shift_window_date(1)" :disabled="!start_time || !end_time" title="Shift window forward one day"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
              </div>
              <div class="mlb-shortcuts-row">
                <span class="mlb-shortcuts-label">Shortcuts:</span>
                <b-button v-for="sc in window_shortcuts" :key="sc.label" size="sm" variant="outline-secondary" @click="apply_shortcut(sc)" :title="sc.start_time + '-' + sc.end_time">
                  {{ sc.label }}
                </b-button>
                <b-button v-if="can_edit_shortcuts" size="sm" variant="outline-secondary" @click="open_edit_shortcuts_modal" title="Edit tracking window shortcuts">
                  <i class="fa fa-pencil" aria-hidden="true"></i>
                </b-button>
              </div>
            </div>

            <div class="mlb-field-group">
              <label class="mlb-field-label">Label (optional)</label>
              <b-form-input
                v-model="label"
                placeholder="e.g. AM Bus Routes"
                size="sm"
                maxlength="100"
                style="width: 220px;"
              ></b-form-input>
            </div>

            <div v-if="!editing_token" class="mlb-field-group">
              <b-form-checkbox v-model="generate_per_vehicle">
                Create unique links for each vehicle
              </b-form-checkbox>
            </div>

            <div>
              <b-button variant="primary" @click="save_link" :disabled="!can_create || creating">
                <span v-if="creating"><b-spinner small></b-spinner> {{ editing_token ? 'Saving...' : 'Generating...' }}</span>
                <span v-else>{{ editing_token ? 'Save Changes' : 'Generate Link' }}</span>
              </b-button>
              <b-button v-if="editing_token" variant="outline-secondary" class="ml-2" @click="cancel_edit">Cancel</b-button>
            </div>
          </div>
        </div>

        <div v-if="created_links.length" style="margin-top: 15px; padding: 10px; background: #d4edda; border-radius: 4px; font-size: 14px;">
          <b-button-close @click="dismiss_created_links" aria-label="Dismiss"></b-button-close>
          <div style="font-weight: bold; margin-bottom: 6px;">
            {{ created_links.length > 1 ? created_links.length + ' Map Links generated' : 'Map Link generated' }}
            <b-button v-if="created_links.length > 1" size="sm" variant="outline-success" class="ml-2" @click="copy_all_created_links">
              <i class="fa fa-clipboard" aria-hidden="true"></i> Copy All
            </b-button>
          </div>
          <div v-for="link in created_links" :key="link.token">
            <strong>{{ link.label || link.token }}:</strong> {{ link.url }}
            <b-button size="sm" variant="outline-success" class="ml-2" @click="copy_to_clipboard(link.url)" title="Copy map link"><i class="fa fa-clipboard" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-success" class="ml-1" :href="link.url" target="_blank" rel="noopener" title="Open in new tab"><i class="fa fa-external-link" aria-hidden="true"></i></b-button>
          </div>
        </div>
      </div>

      <!-- Links Table -->
      <div style="width: 95%; max-width: 1400px;">
        <span style="color: var(--fi-rail-accent); font-weight: bold; font-size: 20px;">Links</span>
        <hr style="margin: 5px 0 15px 0;"/>

        <div style="display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap; margin-bottom: 15px;">
          <div style="min-width: 200px;">
            <label style="font-weight: bold; font-size: 13px; margin-bottom: 2px;">Label</label>
            <b-form-input v-model="filter_label" placeholder="Search label..." size="sm"></b-form-input>
          </div>
          <div>
            <label style="font-weight: bold; font-size: 13px; margin-bottom: 2px;">Window From</label>
            <b-form-input v-model="filter_date_from" type="date" size="sm"></b-form-input>
          </div>
          <div>
            <label style="font-weight: bold; font-size: 13px; margin-bottom: 2px;">Window To</label>
            <b-form-input v-model="filter_date_to" type="date" size="sm"></b-form-input>
          </div>
          <div>
            <label style="font-weight: bold; font-size: 13px; margin-bottom: 2px;">Range</label>
            <div style="display: flex; gap: 4px;">
              <b-button size="sm" variant="outline-secondary" @click="apply_date_range_preset(1)" title="Today through tomorrow">1d</b-button>
              <b-button size="sm" variant="outline-secondary" @click="apply_date_range_preset(7)" title="Today through 7 days out">7d</b-button>
              <b-button size="sm" variant="outline-secondary" @click="apply_date_range_preset(30)" title="Today through 30 days out">30d</b-button>
            </div>
          </div>
          <b-button size="sm" variant="outline-danger" @click="confirm_delete_all" :disabled="filtered_links.length === 0" title="Permanently delete every link matching the current filters">
            <i class="fa fa-trash" aria-hidden="true"></i> Delete All ({{ filtered_links.length }})
          </b-button>
          <div class="mlb-chip-row" style="margin-left: auto; margin-bottom: 0;">
            <span class="mlb-chip mlb-chip-all" :class="{ 'mlb-chip-selected': !filter_status }" @click="set_status_filter('')">All {{ status_counts.all }}</span>
            <span class="mlb-chip mlb-chip-upcoming" :class="{ 'mlb-chip-selected': filter_status === 'upcoming' }" @click="set_status_filter('upcoming')">Upcoming {{ status_counts.upcoming }}</span>
            <span class="mlb-chip mlb-chip-active" :class="{ 'mlb-chip-selected': filter_status === 'active' }" @click="set_status_filter('active')">Active {{ status_counts.active }}</span>
            <span class="mlb-chip mlb-chip-expired" :class="{ 'mlb-chip-selected': filter_status === 'expired' }" @click="set_status_filter('expired')">Expired {{ status_counts.expired }}</span>
          </div>
        </div>

        <div v-if="loading_links" class="text-center" style="padding: 20px;">
          <b-spinner variant="primary"></b-spinner>
        </div>
        <table v-else class="table table-sm table-hover" style="font-size: 14px;">
          <thead style="background: #f8f9fa;">
            <tr>
              <th>Label</th>
              <th>Vehicles</th>
              <th>Window</th>
              <th style="text-align: center;">Status</th>
              <th>Created By</th>
              <th style="text-align: center;">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="link in paginated_links" :key="link.token">
              <td>{{ link.label || '—' }}</td>
              <td style="max-width: 250px;" :title="link.vehicles.join(', ')">
                {{ link.vehicles.slice(0, 3).join(', ') }}<span v-if="link.vehicles.length > 3"> +{{ link.vehicles.length - 3 }} more</span>
              </td>
              <td style="white-space: nowrap;">{{ link.start_display }}<br/>{{ link.end_display }}</td>
              <td style="text-align: center;">
                <b-badge :variant="status_variant(link.status)">{{ link.status }}</b-badge>
              </td>
              <td>{{ link.created_by }}</td>
              <td style="text-align: center; white-space: nowrap;">
                <b-button size="sm" variant="outline-primary" @click="copy_to_clipboard(link.url)" title="Copy map link"><i class="fa fa-clipboard" aria-hidden="true"></i></b-button>
                <b-button size="sm" variant="outline-primary" class="ml-1" :href="link.url" target="_blank" rel="noopener" title="Open in new tab"><i class="fa fa-external-link" aria-hidden="true"></i></b-button>
                <b-button size="sm" variant="outline-secondary" class="ml-1" @click="edit_link(link)" title="Edit"><i class="fa fa-pencil" aria-hidden="true"></i></b-button>
                <b-button size="sm" variant="outline-secondary" class="ml-1" @click="duplicate_link(link)" title="Duplicate"><i class="fa fa-copy" aria-hidden="true"></i></b-button>
                <b-button size="sm" variant="outline-danger" class="ml-1" @click="delete_link(link)" title="Delete map link"><i class="fa fa-trash" aria-hidden="true"></i></b-button>
              </td>
            </tr>
            <tr v-if="filtered_links.length === 0">
              <td colspan="6" style="text-align: center; color: #999; padding: 30px;">
                {{ links.length === 0 ? 'No map links created yet' : 'No map links match your filters' }}
              </td>
            </tr>
          </tbody>
        </table>
        <b-pagination
          v-if="filtered_links.length > links_per_page"
          v-model="current_page"
          :total-rows="filtered_links.length"
          :per-page="links_per_page"
          align="center"
          size="sm"
        ></b-pagination>
      </div>
    </div>

    <!-- Delete All Confirmation -->
    <b-modal v-model="show_delete_all_modal" title="Delete All Matching Links" @ok="delete_all_links" ok-variant="danger" ok-title="Delete All" :ok-disabled="deleting_all">
      <p>Are you sure you want to permanently delete all <strong>{{ filtered_links.length }}</strong> link{{ filtered_links.length === 1 ? '' : 's' }} matching the current filters?
        Anyone holding one of these links will immediately lose access. This cannot be undone.</p>
    </b-modal>

    <!-- Edit Tracking Window Shortcuts -->
    <b-modal
      v-model="show_edit_shortcuts_modal"
      title="Edit Tracking Window Shortcuts"
      size="lg"
      @ok="save_shortcuts"
      ok-title="Save"
      :ok-disabled="!!shortcuts_json_error || saving_shortcuts"
    >
      <p class="mlb-field-hint">Add as many shortcuts as you'd like - they show as buttons under Tracking Window for every user on your team. Start/End Time use 24-hour HH:MM format (e.g. 05:45, 17:30). Removing all rows reverts to the default AM/PM/All Day shortcuts.</p>
      <JsonObjectListEditor
        v-model="shortcuts_json"
        field-key="map_link_shortcuts"
        :columns="shortcuts_columns"
        allow-raw-json
        @validation="on_shortcuts_validation"
      />
    </b-modal>

    <!-- How to Use -->
    <b-modal v-model="show_help" title="How to Use Map Link Builder" size="lg" ok-only scrollable>
      <div class="mlb-help-body">
        <h4>What this page does</h4>
        <p>Create a shareable URL that shows only the vehicles you pick, tracking live for only the time window you set. Anyone with the link can view it &mdash; no fleet-int login required &mdash; but it only works while the window is open.</p>

        <hr/>

        <h4>Creating a link</h4>
        <ol>
          <li><b>Vehicles</b> &mdash; search and click vehicles on the left to add them to your selection (up to 50). Click a selected vehicle to remove it, or use "add all" / "remove all".</li>
          <li><b>Label (optional)</b> &mdash; a name to help you recognize the link later, e.g. "AM Bus Routes".</li>
          <li><b>Tracking Window</b> &mdash; the start and end time, in your company's local time, that the link will show live positions for (max 31 days). Use the shortcut buttons to quickly set common windows for the currently selected date, or the arrows beside the fields to shift both dates forward/back a day while keeping the same times. Tenant admins can click the pencil next to the shortcuts to add, remove, or edit them (any number allowed) for the whole team.</li>
          <li><b>Generate unique links by vehicle</b> (checked by default) &mdash; creates a separate link per selected vehicle instead of one link covering all of them, each labeled "Label-vehicleID" (or just the vehicle ID if no label is set) so every link is easy to tell apart. Uncheck it to generate a single link that tracks every selected vehicle together.</li>
          <li>Click <b>Generate Link</b>. The URL(s) appear above the form, each next to its label &mdash; copy one, open it in a new tab, or use <b>Copy All</b> to copy every generated link at once.</li>
        </ol>

        <hr/>

        <h4>What the recipient sees</h4>
        <p>A simple, mobile-friendly live map with only the selected vehicles &mdash; no login, header, or navigation required. Before the window opens it shows "upcoming"; after it closes it shows "expired" and stops updating.</p>

        <hr/>

        <h4>Managing existing links</h4>
        <ul>
          <li><i class="fa fa-clipboard" aria-hidden="true"></i> <b>Copy</b> &mdash; copies the link URL to your clipboard.</li>
          <li><i class="fa fa-external-link" aria-hidden="true"></i> <b>Open in new tab</b> &mdash; opens the link the way a recipient would see it.</li>
          <li><i class="fa fa-pencil" aria-hidden="true"></i> <b>Edit</b> &mdash; change the vehicles, label, or window on an existing link without changing its URL.</li>
          <li><i class="fa fa-copy" aria-hidden="true"></i> <b>Duplicate</b> &mdash; pre-fills the form from an existing link so you can generate a new link (and new URL) starting from the same values.</li>
          <li><i class="fa fa-trash" aria-hidden="true"></i> <b>Delete</b> &mdash; permanently deletes the link. Anyone holding the URL immediately loses access; this cannot be undone.</li>
          <li><b>Delete All</b> (above the table) &mdash; permanently deletes every link currently matching your Label/Status/Window filters, after a confirmation showing how many will be removed. With no filters applied, this deletes every link for your tenant.</li>
        </ul>

        <hr/>

        <h4>Status</h4>
        <p>
          <b-badge variant="info">upcoming</b-badge> the window has not started yet &mdash;
          <b-badge variant="success">active</b-badge> the window is open and live positions are showing &mdash;
          <b-badge variant="secondary">expired</b-badge> the window has closed.
        </p>

        <hr/>

        <h4>Finding a link</h4>
        <p>Click a status chip (All / Upcoming / Active / Expired) above the table to filter by status &mdash; the number on each chip is a live count. Combine it with the Label and Window From/To filters to narrow further; Window From/To match any link whose tracking window overlaps the selected dates, and the 1d/7d/30d Range buttons quickly set that range to today through 1, 7, or 30 days out. The table defaults to today through 7 days out, showing the 10 most recent matches &mdash; use the paging controls below it to see more.</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import moment from 'moment'
import {
  get_vehicle_list, get_map_links, get_map_link_shortcuts, create_map_link, update_map_link, delete_map_link,
  update_tenant_config_settings,
} from '../networking/apis'
import JsonObjectListEditor from './JsonObjectListEditor.vue'

// falls back to these until load_shortcuts() resolves (or if it fails) -
// mirrors DEFAULT_WINDOW_SHORTCUTS in vehicles/map_link_service.py
const DEFAULT_SHORTCUTS = [
  {label: 'AM', start_time: '05:45', end_time: '08:30'},
  {label: 'PM', start_time: '14:45', end_time: '17:30'},
  {label: 'All Day', start_time: '05:45', end_time: '17:30'},
]

function default_window() {
  // matches the "All Day" tracking window shortcut
  const date = moment().add(1, 'days').format('YYYY-MM-DD')
  return {start: date + 'T05:45', end: date + 'T17:30'}
}

function default_date_range(days) {
  return {
    from: moment().format('YYYY-MM-DD'),
    to: moment().add(days, 'days').format('YYYY-MM-DD'),
  }
}

export default {
  name: 'map_link_builder',
  metaInfo: {
    title: "Fleet Integrations",
    titleTemplate: "Fleet Integrations",
  },
  components: {
    JsonObjectListEditor,
  },
  data() {
    const window = default_window()
    const date_range = default_date_range(7)
    return {
      alert: {
        show: false,
        message: '',
        variant: ''
      },
      vehicle_options: [],
      vehicle_search: '',
      selected_vehicles: [],
      label: '',
      start_time: window.start,
      end_time: window.end,
      generate_per_vehicle: true,
      creating: false,
      created_links: [],
      created_links_timeout: null,
      editing_token: null,
      links: [],
      loading_links: false,
      current_page: 1,
      links_per_page: 10,
      show_help: false,
      show_delete_all_modal: false,
      deleting_all: false,
      filter_label: '',
      filter_status: '',
      filter_date_from: date_range.from,
      filter_date_to: date_range.to,
      window_shortcuts: DEFAULT_SHORTCUTS,
      show_edit_shortcuts_modal: false,
      shortcuts_json: '',
      shortcuts_json_error: '',
      saving_shortcuts: false,
      shortcuts_columns: [
        {key: 'label', label: 'Label', width: '30%', placeholder: 'e.g. AM'},
        {key: 'start_time', label: 'Start Time (HH:MM)', width: '35%', placeholder: '05:45'},
        {key: 'end_time', label: 'End Time (HH:MM)', width: '35%', placeholder: '17:30'},
      ],
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    available_vehicles() {
      const selected = new Set(this.selected_vehicles)
      let list = this.vehicle_options.filter(v => !selected.has(v.value))
      if (this.vehicle_search) {
        const q = this.vehicle_search.toLowerCase()
        list = list.filter(v => v.text.toLowerCase().includes(q))
      }
      return list
    },
    can_create() {
      return this.selected_vehicles.length > 0 && this.start_time && this.end_time
    },
    can_edit_shortcuts() {
      // matches tenant_admin_required on the tenant config settings save endpoint
      return this.user.role === 'admin' || this.user.role === 'customer-admin'
    },
    links_matching_label_and_window() {
      const label_q = this.filter_label.trim().toLowerCase()
      return this.links.filter(link => {
        if (label_q && !(link.label || '').toLowerCase().includes(label_q)) return false
        // window overlap: link's window must touch the selected date range,
        // using the tenant-local date portion of start_input/end_input
        if (this.filter_date_from && link.end_input.split('T')[0] < this.filter_date_from) return false
        if (this.filter_date_to && link.start_input.split('T')[0] > this.filter_date_to) return false
        return true
      })
    },
    status_counts() {
      const counts = {all: 0, upcoming: 0, active: 0, expired: 0}
      for (const link of this.links_matching_label_and_window) {
        counts.all++
        if (counts[link.status] !== undefined) counts[link.status]++
      }
      return counts
    },
    filtered_links() {
      if (!this.filter_status) return this.links_matching_label_and_window
      return this.links_matching_label_and_window.filter(link => link.status === this.filter_status)
    },
    paginated_links() {
      const start = (this.current_page - 1) * this.links_per_page
      return this.filtered_links.slice(start, start + this.links_per_page)
    },
  },
  async created() {
    this.load_page_data()
  },
  methods: {
    async load_page_data() {
      this.load_vehicles()
      this.load_links()
      this.load_shortcuts()
    },
    async load_shortcuts() {
      try {
        const {value: {data}} = await get_map_link_shortcuts(this.user.tenant)
        if (Array.isArray(data) && data.length) this.window_shortcuts = data
      } catch (e) {
        // keep whatever shortcuts are already showing (defaults, or the
        // last successful load) rather than blanking the row on a blip
      }
    },
    async load_vehicles() {
      this.vehicle_options = []
      try {
        const {value: {data = []}} = await get_vehicle_list(this.user.tenant, moment().format('YYYY-MM-DD'))
        this.vehicle_options = data.filter(item => item['text'] !== 'All')
      } catch (e) {
        this.show_alert('error loading vehicles, try logging in again or contact support if needed', 'danger')
      }
    },
    async load_links() {
      this.loading_links = true
      try {
        const {value: {data = []}} = await get_map_links(this.user.tenant)
        this.links = data
        this.current_page = 1
      } catch (e) {
        this.show_alert('error loading map links, try logging in again or contact support if needed', 'danger')
      } finally {
        this.loading_links = false
      }
    },
    build_vehicle_label(vehicle_id) {
      const base = this.label.trim()
      return base ? base + '-' + vehicle_id : vehicle_id
    },
    async save_link() {
      this.creating = true
      this.dismiss_created_links()
      try {
        if (this.editing_token) {
          await update_map_link(this.user.tenant, this.editing_token, {
            label: this.label,
            vehicles: this.selected_vehicles,
            startTime: this.start_time,
            endTime: this.end_time,
          })
          this.show_alert('map link updated', 'success')
          this.cancel_edit()
        } else if (this.generate_per_vehicle) {
          const results = await Promise.allSettled(this.selected_vehicles.map(vehicle_id => create_map_link(this.user.tenant, {
            label: this.build_vehicle_label(vehicle_id),
            vehicles: [vehicle_id],
            startTime: this.start_time,
            endTime: this.end_time,
          })))
          const created = results.filter(r => r.status === 'fulfilled').map(r => r.value.value.data)
          const failed = results.length - created.length
          this.show_created_links(created)
          if (failed) {
            this.show_alert('created ' + created.length + ' of ' + results.length + ' map links; ' + failed + ' failed, try again', failed === results.length ? 'danger' : 'warning')
          }
        } else {
          const {value: {data}} = await create_map_link(this.user.tenant, {
            label: this.label,
            vehicles: this.selected_vehicles,
            startTime: this.start_time,
            endTime: this.end_time,
          })
          this.show_created_links([data])
        }
        this.load_links()
      } catch (e) {
        const message = (e.response && e.response.data && e.response.data.message) || 'error saving map link'
        this.show_alert(message, 'danger')
      } finally {
        this.creating = false
      }
    },
    show_created_links(links) {
      this.created_links = links
      if (!links.length) return
      this.created_links_timeout = setTimeout(this.dismiss_created_links, 15000)
      this.label = ''
      this.selected_vehicles = []
      const window = default_window()
      this.start_time = window.start
      this.end_time = window.end
    },
    copy_all_created_links() {
      const text = this.created_links.map(link => (link.label || link.token) + ': ' + link.url).join('\n')
      this.copy_to_clipboard(text)
    },
    edit_link(link) {
      this.editing_token = link.token
      this.label = link.label || ''
      this.selected_vehicles = [...link.vehicles]
      // start_input/end_input are the tenant-local wall clock the backend
      // already computed (same tz it'll parse startTime/endTime against on
      // save) - using them directly avoids converting through the browser's
      // machine timezone, which may not match the tenant's
      this.start_time = link.start_input
      this.end_time = link.end_input
      this.dismiss_created_links()
      this.scroll_to_form()
    },
    duplicate_link(link) {
      this.editing_token = null
      this.label = link.label ? link.label + ' (copy)' : ''
      this.selected_vehicles = [...link.vehicles]
      this.start_time = link.start_input
      this.end_time = link.end_input
      this.dismiss_created_links()
      this.scroll_to_form()
    },
    cancel_edit() {
      this.editing_token = null
      this.label = ''
      this.selected_vehicles = []
      const window = default_window()
      this.start_time = window.start
      this.end_time = window.end
    },
    scroll_to_form() {
      if (this.$refs.linkForm) this.$refs.linkForm.scrollIntoView({behavior: 'smooth', block: 'start'})
    },
    dismiss_created_links() {
      clearTimeout(this.created_links_timeout)
      this.created_links_timeout = null
      this.created_links = []
    },
    apply_window_preset(start_hour, start_min, end_hour, end_min) {
      // keep whatever date is already picked (from the start field), default today
      const date = this.start_time ? this.start_time.split('T')[0] : moment().format('YYYY-MM-DD')
      this.start_time = date + 'T' + String(start_hour).padStart(2, '0') + ':' + String(start_min).padStart(2, '0')
      this.end_time = date + 'T' + String(end_hour).padStart(2, '0') + ':' + String(end_min).padStart(2, '0')
    },
    apply_shortcut(shortcut) {
      const [start_hour, start_min] = shortcut.start_time.split(':').map(Number)
      const [end_hour, end_min] = shortcut.end_time.split(':').map(Number)
      this.apply_window_preset(start_hour, start_min || 0, end_hour, end_min || 0)
    },
    open_edit_shortcuts_modal() {
      this.shortcuts_json = JSON.stringify(this.window_shortcuts)
      this.shortcuts_json_error = ''
      this.show_edit_shortcuts_modal = true
    },
    on_shortcuts_validation(fieldKey, error) {
      this.shortcuts_json_error = error
    },
    async save_shortcuts(bvModalEvt) {
      bvModalEvt.preventDefault()
      if (this.shortcuts_json_error) return
      this.saving_shortcuts = true
      try {
        await update_tenant_config_settings(this.user.tenant, {map_link_shortcuts: this.shortcuts_json || '[]'})
        this.show_edit_shortcuts_modal = false
        await this.load_shortcuts()
        this.show_alert('tracking window shortcuts updated', 'success')
      } catch (e) {
        this.show_alert('error saving shortcuts, try again or contact support if needed', 'danger')
      } finally {
        this.saving_shortcuts = false
      }
    },
    shift_window_date(days) {
      // keeps the time-of-day on both ends, only moves the calendar date
      if (!this.start_time || !this.end_time) return
      this.start_time = moment(this.start_time).add(days, 'days').format('YYYY-MM-DDTHH:mm')
      this.end_time = moment(this.end_time).add(days, 'days').format('YYYY-MM-DDTHH:mm')
    },
    select_vehicle(veh) {
      this.selected_vehicles.push(veh.value)
    },
    unselect_vehicle(name) {
      this.selected_vehicles = this.selected_vehicles.filter(v => v !== name)
    },
    select_all() {
      // adds what's currently showing, so a search filter scopes "add all"
      this.selected_vehicles = this.selected_vehicles.concat(this.available_vehicles.map(v => v.value))
    },
    set_status_filter(status) {
      this.filter_status = this.filter_status === status ? '' : status
    },
    apply_date_range_preset(days) {
      const range = default_date_range(days)
      this.filter_date_from = range.from
      this.filter_date_to = range.to
    },
    async delete_link(link) {
      try {
        await delete_map_link(this.user.tenant, link.token)
        this.load_links()
      } catch (e) {
        this.show_alert('error deleting map link, try again or contact support if needed', 'danger')
      }
    },
    confirm_delete_all() {
      this.show_delete_all_modal = true
    },
    async delete_all_links(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.deleting_all = true
      const targets = this.filtered_links
      try {
        const results = await Promise.allSettled(targets.map(link => delete_map_link(this.user.tenant, link.token)))
        const succeeded = results.filter(r => r.status === 'fulfilled').length
        const failed = results.length - succeeded
        this.show_delete_all_modal = false
        if (failed) {
          this.show_alert('deleted ' + succeeded + ' of ' + results.length + ' links; ' + failed + ' failed, try again', failed === results.length ? 'danger' : 'warning')
        } else {
          this.show_alert('deleted ' + succeeded + ' link' + (succeeded === 1 ? '' : 's'), 'success')
        }
        this.load_links()
      } finally {
        this.deleting_all = false
      }
    },
    status_variant(status) {
      return {upcoming: 'info', active: 'success', expired: 'secondary'}[status] || 'secondary'
    },
    copy_to_clipboard(text) {
      navigator.clipboard.writeText(text).catch(() => {
        const el = document.createElement('textarea')
        el.value = text
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      })
    },
    show_alert(message, variant) {
      this.alert = {show: true, message: message, variant: variant}
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.cancel_edit()
        this.dismiss_created_links()
        this.filter_label = ''
        this.filter_status = ''
        this.apply_date_range_preset(7)
        this.load_page_data()
      }
    },
    filter_label() {
      this.current_page = 1
    },
    filter_status() {
      this.current_page = 1
    },
    filter_date_from() {
      this.current_page = 1
    },
    filter_date_to() {
      this.current_page = 1
    },
  }
}
</script>
<style scoped>
.mlb-chip-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}

.mlb-chip {
  display: inline-block;
  padding: 4px 10px;
  border: 1px solid #ccc;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 600;
  background-color: #f8f9fa;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}

.mlb-chip:hover {
  opacity: 0.85;
}

.mlb-chip-selected {
  outline: 2px solid #495057;
  outline-offset: 1px;
}

.mlb-chip-upcoming {
  background-color: hsla(200, 70%, 80%, 0.55);
  border-color: #7fa8c9;
}

.mlb-chip-active {
  background-color: hsla(120, 100%, 75%, 0.55);
  border-color: #7dcc7d;
}

.mlb-chip-expired {
  background-color: #e9ecef;
  border-color: #ced4da;
}

.mlb-field-label {
  display: block;
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 6px;
}

.mlb-field-hint {
  font-weight: normal;
  font-size: 12px;
  color: #888;
}

.mlb-window-note {
  font-size: 13px;
  font-weight: bold;
  color: var(--fi-rail-accent);
  margin-top: 8px;
}

.mlb-shortcuts-label {
  font-size: 13px;
  font-weight: bold;
  color: #444;
}

.mlb-field-group {
  margin-bottom: 20px;
}

.mlb-shortcuts-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 12px;
  align-items: center;
  margin-top: 20px;
}

.mlb-shortcuts-row .btn {
  white-space: nowrap;
  flex-shrink: 0;
}

.mlb-veh-grid {
  display: grid;
  grid-template-columns: 190px 38px 190px;
  column-gap: 10px;
  row-gap: 6px;
}

.mlb-veh-warning {
  font-size: 12px;
  color: darkred;
  margin-top: 8px;
}

.mlb-list-header {
  font-size: 13px;
  font-weight: bold;
  color: #666;
  margin-bottom: 2px;
}

.mlb-veh-list {
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
  height: 240px;
  overflow-y: auto;
}

.mlb-transfer-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 240px;
}

.mlb-veh-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 3px 8px;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #f0f0f0;
}

.mlb-veh-item:hover {
  background-color: #dce8f5;
}

.mlb-veh-action {
  color: var(--fi-rail-accent);
  font-weight: bold;
}

.mlb-veh-empty {
  color: #999;
  padding: 10px;
  text-align: center;
  font-size: 13px;
}

.mlb-help-body {
  font-size: 15px;
  line-height: 1.5;
  text-align: left;
}
.mlb-help-body h4 {
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 8px;
}
.mlb-help-body p {
  margin-bottom: 10px;
}
.mlb-help-body ul, .mlb-help-body ol {
  padding-left: 20px;
  margin-bottom: 0;
}
.mlb-help-body li {
  margin-bottom: 6px;
}
</style>
